import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const UnauthPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const getHomeRoute = () => {
    if (!isAuthenticated || !user) return "/auth/login";

    const role = String(user.role || "").toLowerCase();
    switch (role) {
      case "superadmin":
        return "/superAdmin/super-dashboard";
      case "employee":
        return "/admin/dashboard";
      case "corporate":
        return "/corporate/corporate-dashboard";
      case "requestor":
        return "/requestor/dashboard";
      default:
        return "/auth/login";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue via-blue to-blue/90 flex items-center justify-center p-4 font-poppins">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-yellow/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Main card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 md:p-10">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center">
                <div className="w-16 h-16 bg-red-500/30 rounded-full flex items-center justify-center">
                  <i className="fa-solid fa-shield-halved text-4xl text-white" />
                </div>
              </div>
              {/* Lock badge */}
              <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-yellow rounded-full flex items-center justify-center shadow-lg">
                <i className="fa-solid fa-lock text-blue text-sm" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Access Denied
            </h1>
            <p className="text-white/70 text-base leading-relaxed">
              You don't have permission to view this page. This area is restricted based on your current role and access level.
            </p>
          </div>

          {/* Info box */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-yellow/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <i className="fa-solid fa-circle-info text-yellow text-sm" />
              </div>
              <div>
                <p className="text-white/90 text-sm font-medium mb-1">Why am I seeing this?</p>
                <p className="text-white/60 text-sm leading-relaxed">
                  Your account role ({user?.role || "Unknown"}) doesn't have access to the requested resource. Contact your administrator if you believe this is an error.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-arrow-left text-sm" />
              Go Back
            </button>
            <button
              onClick={() => navigate(getHomeRoute())}
              className="flex-1 px-6 py-3 bg-yellow hover:bg-yellow/90 text-blue font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-home text-sm" />
              Go to Dashboard
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-white/40 text-sm">
            Need help?{" "}
            <button
              onClick={() => navigate("/auth/login")}
              className="text-yellow hover:text-yellow/80 font-medium transition-colors"
            >
              Sign in with a different account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthPage;
